import { useEffect, useMemo, useState } from "react";
import { useLocationSelection } from "../../context/LocationContext";
import { useLocationMenu } from "../../hooks/useLocationMenu";
import { formatDate } from "../../utils/formatters";
import MenuItemCard from "../../components/public/MenuItemCard";
import MenuCategorySection from "./MenuCategorySection";
import SelectedLocationBanner from "./SelectedLocationBanner";
import "./MenuPage.css";

function groupItemsByCategory(items) {
  return items.reduce((groups, item) => {
    const categoryName = item.category?.name || "Favorites";

    return {
      ...groups,
      [categoryName]: [...(groups[categoryName] || []), item],
    };
  }, {});
}

function MenuPage({ locationSlug, onChooseLocation }) {
  const { selectedLocation, chooseLocation, locations, addCartItem } = useLocationSelection();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const effectiveLocationSlug = locationSlug || selectedLocation?.slug || "";
  const { menuData, loading, error } = useLocationMenu(effectiveLocationSlug);

  const pageLocation = menuData?.location || selectedLocation;
  const regularItems = useMemo(() => {
    return menuData?.regular_items || [];
  }, [menuData?.regular_items]);
  const weeklyItems = menuData?.weekly_menu?.items || [];

  useEffect(() => {
    if (!locationSlug && selectedLocation?.slug && window.location.pathname === "/menu") {
      window.history.replaceState({}, "", `/menu/${selectedLocation.slug}`);
    }
  }, [locationSlug, selectedLocation]);

  useEffect(() => {
    if (menuData?.location && selectedLocation?.slug !== menuData.location.slug) {
      chooseLocation(menuData.location);
    }
  }, [chooseLocation, menuData, selectedLocation]);

  const filteredItems = regularItems.filter((item) => {
    const matchesSearch = [item.name, item.description, item.category?.name]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "all" || item.category?.slug === activeCategory;

    return matchesSearch && matchesCategory;
  });

  const categories = useMemo(() => {
    return Array.from(
      new Map(
        regularItems.map((item) => [item.category?.slug || "favorites", item.category]),
      ).entries(),
    ).map(([slug, category]) => ({
      slug,
      name: category?.name || "Favorites",
    }));
  }, [regularItems]);

  const groupedItems = groupItemsByCategory(filteredItems);
  const hasSearchOrCategoryFilter = Boolean(searchTerm.trim()) || activeCategory !== "all";
  const regularMenuEmptyText = hasSearchOrCategoryFilter
    ? "No regular menu items match that search."
    : `No regular menu items are available for ${pageLocation?.name || "this location"} yet.`;
  const matchingLocation = locations.find((location) => location.slug === effectiveLocationSlug);
  const invalidLocation = !loading && !error && !pageLocation && !matchingLocation;

  if (!effectiveLocationSlug) {
    return (
      <main className="section public-menu-page">
        <div className="container empty-state">
          <strong>Choose a location first.</strong>
          <span>Menus are shown by location so availability and prices stay accurate.</span>
          <button className="btn btn-primary" onClick={onChooseLocation} type="button">
            Choose a Location
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="section public-menu-page">
      <div className="container">
        {loading ? <div className="empty-state">Loading this location&apos;s menu...</div> : null}

        {error || invalidLocation ? (
          <div className="empty-state">
            <strong>We could not find that menu.</strong>
            <span>Please choose an active Zayas Kitchen location.</span>
            <button className="btn btn-primary" onClick={onChooseLocation} type="button">
              Choose a Location
            </button>
          </div>
        ) : null}

        {!loading && !error && pageLocation ? (
          <>
            <SelectedLocationBanner location={pageLocation} onChangeLocation={onChooseLocation} />

            <section className="public-weekly-menu" aria-labelledby="weekly-menu-title">
              <div className="public-section-row">
                <div>
                  <p className="eyebrow">Weekly Specials</p>
                  <h2 id="weekly-menu-title">This week&apos;s menu</h2>
                  {menuData?.weekly_menu ? (
                    <p className="muted-text">
                      {formatDate(menuData.weekly_menu.start_date)} to {formatDate(menuData.weekly_menu.end_date)}
                    </p>
                  ) : null}
                </div>
              </div>

              {weeklyItems.length ? (
                <div className="public-card-grid public-card-grid--three">
                  {weeklyItems.map((item) => (
                    <MenuItemCard key={item.id} item={item} onAdd={addCartItem} />
                  ))}
                </div>
              ) : (
                <div className="empty-state">No published weekly specials are available for this location yet.</div>
              )}
            </section>

            <section className="public-regular-menu" aria-labelledby="regular-menu-title">
              <div className="public-section-row">
                <div>
                  <p className="eyebrow">Regular Menu</p>
                  <h2 id="regular-menu-title">Comfort favorites</h2>
                </div>
                <label className="public-menu-search">
                  <span className="visually-hidden">Search menu</span>
                  <input
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search dishes or categories"
                    type="search"
                    value={searchTerm}
                  />
                </label>
              </div>

              <div className="menu-category-tabs" role="tablist" aria-label="Menu categories">
                <button
                  aria-selected={activeCategory === "all"}
                  className="menu-category-tab"
                  onClick={() => setActiveCategory("all")}
                  role="tab"
                  type="button"
                >
                  All
                </button>
                {categories.map((category) => (
                  <button
                    aria-selected={activeCategory === category.slug}
                    className="menu-category-tab"
                    key={category.slug}
                    onClick={() => setActiveCategory(category.slug)}
                    role="tab"
                    type="button"
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {filteredItems.length ? (
                Object.entries(groupedItems).map(([categoryName, items]) => (
                  <MenuCategorySection
                    categoryName={categoryName}
                    items={items}
                    key={categoryName}
                    onAddItem={addCartItem}
                  />
                ))
              ) : (
                <div className="empty-state">{regularMenuEmptyText}</div>
              )}
            </section>
          </>
        ) : null}
      </div>
    </main>
  );
}

export default MenuPage;